import React from "react";
import { AreaChart as RechartsAreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
import { CardHeader, CardTitle, CardDescription } from "../../shadcn/card";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip } from "../../shadcn/chart";
import { formatNumber } from "../../../utils/formatNumber";
import { usePartnerStore } from "store/partnerStore";

interface AreaChartProps {
  data: { label: string; value: number }[];
  title?: string;
  description?: string;
  lineColor?: string;
  showCurrency?: boolean;
  showLegend?: boolean;
  legendLabel?: string;
}

export const AreaChart: React.FC<AreaChartProps> = ({ data, title, description, lineColor = "var(--chart-4)", showCurrency = false, showLegend = false, legendLabel = "Series" }: AreaChartProps) => {
  const { partnerConfig } = usePartnerStore();
  const currency = partnerConfig?.defaultCurrency;

  // If no data, return placeholder
  if (!data || data.length === 0) {
    return (
      <div className="w-full h-[250px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mb-4 text-gray-400 mx-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
              />
            </svg>
          </div>
          <p className="text-gray-500 text-sm">No data available</p>
        </div>
      </div>
    );
  }

  // Get a reliable color - try CSS variable first, then fallback to a good default
  const getEffectiveColor = () => {
    if (lineColor.startsWith("var(--")) {
      const computedStyle = getComputedStyle(document.documentElement);
      const cssValue = computedStyle.getPropertyValue(
        lineColor.replace("var(", "").replace(")", "")
      );

      if (cssValue && cssValue.trim() !== "") {
        return cssValue.trim();
      }
    }

    return "#3b82f6";
  };

  const effectiveLineColor = getEffectiveColor();

  // Chart configuration for the legend
  const chartConfig = {
    value: {
      label: legendLabel,
      color: effectiveLineColor,
    },
  };

  const rotateTicks = data.length > 15;

  return (
    <div className="w-full h-[250px] flex">
      {title && (
        <CardHeader className="pl-0 pb-4">
          <CardTitle className="text-sm">{title}</CardTitle>
          <CardDescription className="text-xs">{description}</CardDescription>
        </CardHeader>
      )}
      <ChartContainer
        config={chartConfig}
        className="w-full !bg-transparent !border-none flex"
      >
        <RechartsAreaChart data={data} margin={{ left: -8, right: 12, bottom: rotateTicks ? 15 : 0 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#e5e7eb"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => value}
            tick={{ fontSize: 10 }}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            interval={0}
            tick={{ fontSize: 10 }}
            angle={rotateTicks ? -45 : 0}
            textAnchor={rotateTicks ? "end" : "middle"}
          />
          <ChartTooltip cursor={false}
            content={({ active, payload, label }) => {
              if (!active || !payload || !payload.length) return null;
              return (
                <div className="bg-white/95 backdrop-blur-sm text-sm border border-gray-200/50 shadow-xl rounded-xl p-4">
                  <p className="text-gray-800 text-xs font-medium uppercase tracking-wide mb-3">{label}</p>
                  <p className="text-gray-800 text-sm font-semibold">{formatNumber(payload[0].value as number)} {showCurrency ? currency : ""}</p>
                </div>
              );
            }} />
          <Area
            dataKey="value"
            type="monotone"
            stroke={effectiveLineColor}
            strokeWidth={2}
            fill={effectiveLineColor}
            fillOpacity={0.1}
          />
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
        </RechartsAreaChart>
      </ChartContainer>
    </div>
  );
};
